/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import ADIMinimumQtyModal from "@NA/Components/ADIMinimumQtyModal";
import AddToCartIcon from "@NA/Icons/AddToCartIcon";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { checkAllowedQuantity } from "@NA/Utils/Utility";
import MiniCartModal from "@NA/Widgets/Cart/MiniCartModal";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    btnIsHidden?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: CustomApplicationState, props: OwnProps & HasProductContext) => {
    const currentPage = getCurrentPage(state).type;
    const isPdp = currentPage === "ProductDetailsPage";
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        addingProductToCart: state.context.isUpdatingCart,
        addToCartLoading: state.context.isUpdatingCart,
        isPdp,
        showADIAddToCart: (adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const ADIRecenlyViewedProductAddToCartButton = ({
    productContext: {
        product,
        product: { id: productId, allowZeroPricing, quoteRequired, productNumber, properties },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, inventory },
        onQtyOrderedChanged,
    },
    productSettings,
    hasEnoughInventory,
    addingProductToCart,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    addToCartLoading,
    btnIsHidden,
    isPdp,
    showADIAddToCart,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [isMiniCartModalOpen, setIsMiniCartModalOpen] = useState(false);
    const [cartLineState, setCartLineState] = useState<CartLineModel>();
    const [selectedProduct, setSelectedProduct] = useState("");

    const [isMinimumQtyOpen, setIsMinimumQtyOpen] = useState(false);

    const openMiniCartHandler = () => setIsMiniCartModalOpen(true);

    const closeMiniCartHandler = () => {
        setIsMiniCartModalOpen(false);
        setCartLineState(undefined);
    };

    const closeMinimumQtyCard = () => setIsMinimumQtyOpen(false);


    const addToCartClickHandler = async () => {
        sessionStorage.setItem("item_list_name", "Product List Page");
        if (isPdp) {
            sessionStorage.setItem("item_list_name", "Product Detail Page");
            if (!checkAllowedQuantity(Number(qtyOrdered), Number(properties.minimuM_QTY))) {
                setIsMinimumQtyOpen(true);
                return;
            }
        }
        setSelectedProduct(productNumber);
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const cartPrice =
            !hasEnoughInventory || !canAddToCart || pricing === undefined || pricing.actualPrice < 0.01
                ? undefined
                : getUnitNetPrice(pricing as ProductPriceDto, qtyOrdered || 1).priceDisplay;

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId,
            qtyOrdered: !parseInt(properties.minimuM_QTY) ? 1 : parseInt(properties.minimuM_QTY),
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (productSettings.showAddToCartConfirmationDialog && cartline) {
            setCartLineState(cartline);
            openMiniCartHandler();
            setSelectedProduct("");
        }
    };

    return (
        <>
            {!btnIsHidden && showADIAddToCart && (
                <Button {...styles} onClick={addToCartClickHandler} {...otherProps} disabled={addToCartLoading}>
                    {addToCartLoading && selectedProduct === productNumber ? (
                        <StyledWrapper>
                            <img src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif" />
                        </StyledWrapper>
                    ) : (
                        <Icon src={AddToCartIcon} color="primary" />
                    )}
                    {translate("Add to Cart")}
                </Button>
            )}

            {cartLineState && (
                <MiniCartModal
                    isModalOpen={isMiniCartModalOpen}
                    onModalClose={closeMiniCartHandler}
                    cartLine={cartLineState}
                />
            )}

            <ADIMinimumQtyModal
                headline={"Add to Cart Message"}
                isOpen={isMinimumQtyOpen}
                errorStyle={false}
                handleClose={closeMinimumQtyCard}
            >
                <>Must be purchased in quantities of {properties.minimuM_QTY}</>
            </ADIMinimumQtyModal>
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ADIRecenlyViewedProductAddToCartButton));
